import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useDiagnosticReceipt,
  useUpdateDiagnosticReceipt,
  useDeleteDiagnosticReceipt,
  useActiveDiagnosticTests,
  usePatientDueReceipts,
  useMergeDiagnosticReceipts,
  useSettings,
} from '../../services/httpServices/queries';
import { diagnosticService } from '../../services/httpServices/diagnosticService';
import { getDiagnosticReceiptPrinter } from '../../utils/devices';
import { printPdfToDevice } from '../../utils/printDocument';
import { PrinterTabs } from '../../components/ui/PrinterTabs';
import { usePrinterTab } from '../../hooks/usePrinterTab';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import { usePermissions } from '../../hooks/usePermissions';
import { DueReceiptPromptModal } from '../../components/ui/DueReceiptPromptModal';
import { ReferralSearchSelect } from '../../components/ui/ReferralSearchSelect';
import { QuickReferralModal } from '../../components/ui/QuickReferralModal';
import { DoctorSearchSelect } from '../../components/ui/DoctorSearchSelect';
import { QuickDoctorModal } from '../../components/ui/QuickDoctorModal';
import { InfoHint } from '../../components/ui/InfoHint';
import { joinFullName } from '../../utils/patientName';
import type { ReferralPartner } from '../../services/httpServices/referralService';
import {
  Loader2,
  AlertCircle,
  X,
  Search,
  ArrowLeft,
  Receipt,
  ClipboardList,
  CheckCircle2,
  FlaskConical,
  Printer,
  Download,
  Save,
  User,
  Calendar,
  Hash,
  History,
  Trash2,
  Info,
  Activity,
  Globe,
} from 'lucide-react';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { ReceivedExcessModal } from '../../components/ui/ReceivedExcessModal';
import { ReceiptDiscountLimitModal } from '../../components/ui/ReceiptDiscountLimitModal';
import { ReferralZeroPayoutModal } from '../../components/ui/ReferralZeroPayoutModal';
import { ReferralCalculationModal } from '../../components/ui/ReferralCalculationModal';
import { Pagination } from '../../components/ui/Pagination';
import { DatePicker, DateTimePicker, toDateInputValue } from '../../components/ui/DatePicker';
import {
  DEFAULT_HOSPITAL_TIMEZONE,
  datetimeLocalToISOInTimeZone,
  toDatetimeLocalValueInTimeZone,
} from '../../lib/timezones';

/**
 * Splits a whole-taka referral total across test lines so the calculation
 * dialog's lines add up exactly to the rounded payout (largest remainder).
 */
function allocateWholeTaka(total: number, weights: number[]): number[] {
  const target = Math.max(0, Math.round(Number(total) || 0));
  const result = weights.map(() => 0);
  const totalWeight = weights.reduce(
    (sum, weight) => sum + Math.max(0, Number(weight) || 0),
    0,
  );
  if (target === 0 || weights.length === 0 || totalWeight <= 0) return result;

  const fractions: Array<{ index: number; fraction: number }> = [];
  let assigned = 0;
  weights.forEach((weight, index) => {
    const ideal = (target * Math.max(0, Number(weight) || 0)) / totalWeight;
    const whole = Math.floor(ideal + 1e-9);
    result[index] = whole;
    assigned += whole;
    fractions.push({ index, fraction: ideal - whole });
  });

  let remainder = target - assigned;
  fractions.sort((a, b) => b.fraction - a.fraction);
  while (remainder > 0) {
    let placed = false;
    for (const { index } of fractions) {
      if (remainder === 0) break;
      result[index] += 1;
      remainder -= 1;
      placed = true;
    }
    if (!placed) break;
  }
  return result;
}

/**
 * Stable signature of the currently selected tests (id, quantity and per-line
 * discount) so a save that only touches payment/discount meta does not resend
 * the test list. Resending it makes the backend delete and re-create every
 * receipt item, lab/radiology order and specimen — unnecessary work that also
 * requires a visit and can fail for older receipts.
 */
function buildItemsSignature(
  items: Map<string, { qty: number; discountPercent: number }>,
): string {
  return Array.from(items.entries())
    .map(([testId, data]) => `${testId}:${Number(data.qty) || 1}:${Number(data.discountPercent) || 0}`)
    .sort()
    .join('|');
}

/**
 * Stable signature of the accessory lines (id + quantity) so a save that only
 * touches tests still resends the accessories. Without this the backend rebuild
 * would delete the accessory charges entirely.
 */
function buildAccessorySignature(
  items: Array<{ diagnosticAccessoryId: string; quantity: number }>,
): string {
  return items
    .map((a) => `${a.diagnosticAccessoryId}:${Number(a.quantity) || 1}`)
    .sort()
    .join('|');
}

/**
 * Resolved amount of the counter extra discount. A percentage is taken from the
 * gross total and capped at 100%; a flat amount never exceeds the bill. Kept in
 * one place so the live preview and the "Received follows the discount" handler
 * can never disagree.
 */
function computeExtraDiscountAmount(
  type: 'FLAT' | 'PERCENT',
  value: number,
  totalAmount: number,
): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (type === 'PERCENT') {
    return Number(((totalAmount * Math.min(value, 100)) / 100).toFixed(2));
  }
  return Number(Math.min(value, totalAmount).toFixed(2));
}

export function DiagnosticReceiptViewEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error } = useDiagnosticReceipt(id);
  const updateMutation = useUpdateDiagnosticReceipt();
  const deleteMutation = useDeleteDiagnosticReceipt();
  const mergeMutation = useMergeDiagnosticReceipts();
  const { data: testsData } = useActiveDiagnosticTests();
  const { data: settingsData } = useSettings();

  const receipt = data?.data;
  const tests = testsData?.data || [];

  // Hospital's allocated base discount for referred receipts (Settings →
  // General). The referral's auto share = base − patient item discount.
  const referralBasePercent = useMemo(() => {
    const rawValue = settingsData?.data?.find(
      (s: any) => s.key === 'referral_base_discount_percent',
    )?.value;
    const raw =
      rawValue === undefined || rawValue === null || String(rawValue).trim() === ''
        ? Number.NaN
        : Number(rawValue);
    return Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 40;
  }, [settingsData]);

  // Hospital timezone configured on Settings → General (`hospital_timezone`).
  // Stays `undefined` until settings settle so the receipt date field seeds from
  // the configured zone rather than the browser's.
  const hospitalTimezone = useMemo(() => {
    if (!settingsData) return undefined;
    const value = settingsData.data?.find((s: any) => s.key === 'hospital_timezone')?.value;
    return (typeof value === 'string' ? value.trim() : '') || DEFAULT_HOSPITAL_TIMEZONE;
  }, [settingsData]);

  // Roles holding the override permission may set the Receipt Base Discount for
  // THIS receipt (defaults to 100%); without it the configured base applies.
  const { hasPermission } = usePermissions();
  const canOverrideBaseDiscount = hasPermission(
    'diagnostics:receipt:override_base_discount',
  );
  // Backdating an existing receipt (moving its business date) is the same
  // per-role capability as creating a backdated one. Without it the date field
  // is shown read-only and the server keeps the stored date.
  const canBackdate = hasPermission('diagnostics:receipt:backdate');
  const [baseDiscountOverride, setBaseDiscountOverride] = useState<number>(100);
  const effectiveBasePercent = canOverrideBaseDiscount
    ? Math.max(0, Math.min(100, Number(baseDiscountOverride) || 0))
    : referralBasePercent;

  // Only a receipt that still owes money can absorb the patient's other unpaid
  // receipts. A fully paid receipt is settled (its revenue accrual and payouts
  // are already booked), so it is never a merge target and never prompts.
  const receiptHasBalance = Number(receipt?.dueAmount) > 0;

  // Other unpaid/partly paid receipts of this patient, matched by MOBILE
  // NUMBER (not name) so duplicate patient records are caught too. The open
  // receipt is excluded: it is the one being edited.
  const { data: patientDueReceipts = [] } = usePatientDueReceipts(
    receipt
      ? {
          id: receipt.patientId,
          mobileNumber: receipt.patient?.mobileNumber || receipt.patient?.mobile,
        }
      : undefined,
    { excludeReceiptId: id, enabled: Boolean(receipt) && receiptHasBalance },
  );
  // Once the counter chooses "keep editing" — or the prompt itself navigated us
  // to another receipt — the prompt stays closed for this receipt until the
  // page is opened again.
  const openedFromDuePrompt = Boolean(
    (location.state as { openedFromDuePrompt?: boolean } | null)?.openedFromDuePrompt,
  );
  const [skipDueReceiptPrompt, setSkipDueReceiptPrompt] = useState(openedFromDuePrompt);
  useEffect(() => {
    setSkipDueReceiptPrompt(openedFromDuePrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-evaluated per navigation
  }, [id, location.key]);

  const [selectedTests, setSelectedTests] = useState<Map<string, { qty: number; discountPercent: number }>>(new Map());
  // Accessory (consumable/container) lines carried on the receipt. Seeded from
  // the stored items so the summary card and the Due match the printed receipt,
  // and editable so counter staff can adjust/remove them like on the create form.
  const [accessoryLines, setAccessoryLines] = useState<
    Map<string, { accessoryId: string; name: string; price: number; qty: number }>
  >(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDepartment, setActiveDepartment] = useState<string>('ALL');
  const [testsPage, setTestsPage] = useState(1);
  const TESTS_PER_PAGE = 20;
  const [paymentMode, setPaymentMode] = useState('cash');
  const [transactionReference, setTransactionReference] = useState('');
  // Promised date for the outstanding due (tracking only — collected money is
  // always dated on the actual payment date). Moved off the list view so the
  // promised date lives on this detail/edit page.
  const [dueDate, setDueDate] = useState('');
  // Receipt date & time — the receipt's business date (`createdAt`). Seeded from
  // the stored date and only sent back when the counter changes it with the
  // "Add Backdate Receipt" permission.
  const [receiptDate, setReceiptDate] = useState('');
  const [receiptDateError, setReceiptDateError] = useState<string | undefined>();
  const seededReceiptTimeRef = useRef(false);
  const originalReceiptDateRef = useRef('');
  // Referral — a single payee that may be a Doctor, an internal Employee or an
  // outside Other referrer.
  const [referralId, setReferralId] = useState('');
  const [referralName, setReferralName] = useState('');
  const [referralType, setReferralType] = useState<'DOCTOR' | 'EMPLOYEE' | 'OTHER' | ''>('');
  // Which referral kind the selector is filtered to (Doctor / Employee / Other).
  const [referralTypeFilter, setReferralTypeFilter] = useState<'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER'>('ALL');
  const [referralSharePercent, setReferralSharePercent] = useState<number>(0);
  const [referralShareTouched, setReferralShareTouched] = useState(false);
  // "How is this calculated?" dialog for the referral commission.
  const [showReferralInfo, setShowReferralInfo] = useState(false);
  // Reference-only doctor — informational, never part of revenue share.
  const [doctorReferenceId, setDoctorReferenceId] = useState('');
  const [doctorReferenceName, setDoctorReferenceName] = useState('');
  const [showQuickReferralModal, setShowQuickReferralModal] = useState(false);
  const [quickReferralQuery, setQuickReferralQuery] = useState('');
  // Quick add-doctor dialog for the Doctor Reference field.
  const [showQuickDoctorModal, setShowQuickDoctorModal] = useState(false);
  const [quickDoctorQuery, setQuickDoctorQuery] = useState('');
  const [doctorRecommendation, setDoctorRecommendation] = useState('');
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [extraDiscountType, setExtraDiscountType] = useState<'FLAT' | 'PERCENT'>('FLAT');
  const [extraDiscountValue, setExtraDiscountValue] = useState<number>(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPayableWarning, setShowPayableWarning] = useState(false);
  const [showReferralPayoutWarning, setShowReferralPayoutWarning] = useState(false);
  const [showDiscountLimitWarning, setShowDiscountLimitWarning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Signature of the tests as loaded from the server, so a save that does not
  // change the test list can omit `items` and avoid the destructive backend
  // replace-items path.
  const originalItemsSignature = useRef<string>('');
  // Same idea for the accessory lines so an untouched accessory set is not
  // needlessly resent (and a changed one is always resent).
  const originalAccessorySignature = useRef<string>('');

  // Print target selected via the ?printer= tab strip, defaulting to the
  // printer designated for diagnostic receipts.
  const diagnosticReceiptPrinter = getDiagnosticReceiptPrinter();
  const { activeDevice } = usePrinterTab(diagnosticReceiptPrinter?.id);

  // Seed selected tests from existing receipt
  useEffect(() => {
    if (receipt) {
      const map = new Map<string, { qty: number; discountPercent: number }>();
      (receipt.items || []).forEach((item: any) => {
        if (item.diagnosticTestId) {
          map.set(item.diagnosticTestId, {
            qty: item.quantity || 1,
            discountPercent: item.discountPercent || 0,
          });
        }
      });
      setSelectedTests(map);
      originalItemsSignature.current = buildItemsSignature(map);

      // Seed the accessory lines from the stored receipt items so the summary
      // (and Due) reflect the accessories that were actually charged.
      const accMap = new Map<
        string,
        { accessoryId: string; name: string; price: number; qty: number }
      >();
      (receipt.items || []).forEach((item: any) => {
        const accId = item.diagnosticAccessoryId || item.accessory?.id;
        if (item.isAccessory && accId && !accMap.has(accId)) {
          accMap.set(accId, {
            accessoryId: accId,
            name: item.accessory?.name || item.accessoryName || 'Accessory',
            price: Number(item.unitPrice) || 0,
            qty: Number(item.quantity) || 1,
          });
        }
      });
      setAccessoryLines(accMap);
      originalAccessorySignature.current = buildAccessorySignature(
        Array.from(accMap.values()).map((a) => ({
          diagnosticAccessoryId: a.accessoryId,
          quantity: a.qty,
        })),
      );

      setPaymentMode(receipt.paymentMode || 'cash');
      setTransactionReference(receipt.transactionReference || '');
      setDueDate(toDateInputValue(receipt.dueDate));
      setReferralId(receipt.referralId || '');
      setReferralName(receipt.referral?.name || '');
      setReferralType((receipt.referral?.type as 'DOCTOR' | 'EMPLOYEE' | 'OTHER') || '');
      setReferralSharePercent(
        receipt.referralSharePercent !== undefined && receipt.referralSharePercent !== null
          ? Number(receipt.referralSharePercent)
          : Number(receipt.referral?.defaultSharePercent) || 0,
      );
      // The auto share (base − item discount) is applied after the items load;
      // the counter can still override it by editing the field.
      setReferralShareTouched(false);
      setDoctorReferenceId(receipt.doctorReferenceId || '');
      setDoctorReferenceName(receipt.doctorReferenceName || '');
      setDoctorRecommendation(receipt.doctorRecommendation || '');
      setPaidAmount(receipt.paidAmount || 0);
      // Restore the saved extra-discount input. Legacy receipts only carry the
      // resolved amount, so fall back to treating it as a flat discount.
      const storedType = receipt.extraDiscountType === 'PERCENT' ? 'PERCENT' : 'FLAT';
      const storedValue = Number(receipt.extraDiscountValue || 0);
      const legacyAmount = Number(receipt.extraDiscountAmount || 0);
      setExtraDiscountType(storedType);
      setExtraDiscountValue(storedValue > 0 ? storedValue : legacyAmount);
    }
  }, [receipt]);

  // Seed the editable receipt date & time from the stored business date. The
  // hospital timezone may arrive after the receipt; while the counter has not
  // touched the field, a late timezone still corrects the seeded value. Once it
  // is edited (or saved) the seed never overwrites the deliberate backdate.
  useEffect(() => {
    if (!receipt) return;
    const zone = hospitalTimezone ?? DEFAULT_HOSPITAL_TIMEZONE;
    const local = toDatetimeLocalValueInTimeZone(new Date(receipt.createdAt), zone);
    if (!seededReceiptTimeRef.current || receiptDate === originalReceiptDateRef.current) {
      seededReceiptTimeRef.current = true;
      originalReceiptDateRef.current = local;
      setReceiptDate(local);
    }
  }, [receipt, hospitalTimezone, receiptDate]);

  /* ── departments derived from tests ── */
  const departments = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number }>();
    tests.forEach((t: any) => {
      const id = t.diagnosticDepartmentId || t.department?.id;
      const name = t.department?.name || 'Other';
      if (!id) return;
      const existing = map.get(id);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(id, { id, name, count: 1 });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [tests]);

  /* ── filter tests by department & search ── */
  const filteredTests = useMemo(() => {
    let list = tests;
    if (activeDepartment !== 'ALL') {
      list = list.filter((t: any) => (t.diagnosticDepartmentId || t.department?.id) === activeDepartment);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((t: any) => t.name?.toLowerCase().includes(q));
    }
    return list;
  }, [tests, activeDepartment, searchQuery]);

  /* ── client-side pagination over the filtered tests ── */
  useEffect(() => {
    setTestsPage(1);
  }, [searchQuery, activeDepartment]);

  const totalFilteredTests = filteredTests.length;
  const totalTestPages = Math.max(1, Math.ceil(totalFilteredTests / TESTS_PER_PAGE));
  const pagedTests = useMemo(() => {
    const start = (testsPage - 1) * TESTS_PER_PAGE;
    return filteredTests.slice(start, start + TESTS_PER_PAGE);
  }, [filteredTests, testsPage]);

  /* ── group by department when viewing ALL ── */
  const groupedTests = useMemo(() => {
    if (activeDepartment !== 'ALL') return null;
    const groups = new Map<string, { dept: { id: string; name: string }; tests: any[] }>();
    pagedTests.forEach((t: any) => {
      const id = t.diagnosticDepartmentId || t.department?.id || 'other';
      const name = t.department?.name || 'Other';
      if (!groups.has(id)) groups.set(id, { dept: { id, name }, tests: [] });
      groups.get(id)!.tests.push(t);
    });
    return Array.from(groups.values()).sort((a, b) => a.dept.name.localeCompare(b.dept.name));
  }, [pagedTests, activeDepartment]);

  // The tests in the exact order they are painted on screen (grouped or paged).
  // Keyboard navigation maps its highlight index onto this list.
  const orderedTests = useMemo(() => {
    if (activeDepartment === 'ALL' && groupedTests) return groupedTests.flatMap((group) => group.tests);
    return pagedTests;
  }, [activeDepartment, groupedTests, pagedTests]);

  const testNavIndex = useMemo(() => {
    const map = new Map<string, number>();
    orderedTests.forEach((test: any, index: number) => map.set(test.id, index));
    return map;
  }, [orderedTests]);

  /* ── selected line items ── */
  const selectedItems = useMemo(() => {
    return Array.from(selectedTests.entries())
      .map(([testId, data]) => {
        const test = tests.find((t: any) => t.id === testId);
        return test ? { testId, qty: data.qty, discountPercent: data.discountPercent, test } : null;
      })
      .filter(Boolean) as { testId: string; qty: number; discountPercent: number; test: any }[];
  }, [selectedTests, tests]);

  // Accessory lines charged on the receipt. They carry no per-line discount and
  // their price is the snapshot stored on the receipt.
  const accessoryItems = useMemo(
    () => Array.from(accessoryLines.values()),
    [accessoryLines],
  );
  const accessoryTotalAmount = accessoryItems.reduce(
    (sum, item) => sum + (item.price || 0) * item.qty,
    0,
  );

  const testsTotalAmount = selectedItems.reduce((sum, item) => sum + (item.test.price || 0) * item.qty, 0);
  const totalAmount = testsTotalAmount + accessoryTotalAmount;

  // Per-item percentage discounts.
  const totalDiscountAmount = selectedItems.reduce((sum, item) => {
    return sum + ((item.test.price || 0) * item.qty * item.discountPercent / 100);
  }, 0);

  // Accessories carry no discount, so the shown rate must be based on the
  // discount-eligible tests only (20% stays 20%, not 170/880 = 19.32%).
  const itemDiscountPercent =
    testsTotalAmount > 0
      ? Number(((totalDiscountAmount / testsTotalAmount) * 100).toFixed(2))
      : 0;

  // Referral's default share = allocated base discount − patient item discount.
  const autoReferralPercent = Number(
    Math.max(0, Math.min(100, effectiveBasePercent - itemDiscountPercent)).toFixed(2),
  );
  useEffect(() => {
    if (referralId && !referralShareTouched) {
      setReferralSharePercent(autoReferralPercent);
    }
  }, [autoReferralPercent, referralId, referralShareTouched]);

  // Extra discount (flat or percentage of the gross total), combined with the
  // item discounts before the due amount is derived.
  const extraDiscountAmount = useMemo(
    () => computeExtraDiscountAmount(extraDiscountType, extraDiscountValue, totalAmount),
    [extraDiscountType, extraDiscountValue, totalAmount],
  );

  const combinedDiscountAmount = Number((totalDiscountAmount + extraDiscountAmount).toFixed(2));
  const combinedDiscountPercent =
    testsTotalAmount > 0
      ? Number(((combinedDiscountAmount / testsTotalAmount) * 100).toFixed(2))
      : 0;

  // The receipt base discount (Settings → General) is the hospital's maximum
  // discount budget, so the receipt's total discount can never exceed it.
  const discountExceedsBase =
    combinedDiscountPercent > effectiveBasePercent + 1e-9;

  const effectiveReferralPercent = Math.max(0, Math.min(100, Number(referralSharePercent) || 0));

  // Referral commission = the referral's share % of the discount-eligible
  // (test) gross. The patient keeps the whole entered discount — the commission
  // is a HOSPITAL expense paid from the allocated base, so it is never
  // subtracted from the patient's discount or added to the bill.
  // Whole taka, mirroring the backend.
  const referralCommissionAmount =
    referralId && effectiveReferralPercent > 0
      ? Math.min(
          Math.round((testsTotalAmount * effectiveReferralPercent) / 100),
          Math.floor(testsTotalAmount + 1e-9),
        )
      : 0;
  const patientDiscountAmount = combinedDiscountAmount;

  // Amount payable after the patient's full discount. The referral commission is
  // a hospital expense and does not affect the patient's payable.
  const netPayableAmount = Math.max(0, Number((totalAmount - patientDiscountAmount).toFixed(2)));
  const dueAmount = Math.max(0, Number((netPayableAmount - paidAmount).toFixed(2)));
  const excessAmount = Math.max(0, Number((paidAmount - netPayableAmount).toFixed(2)));
  const isExcessReceived = excessAmount > 0;
  const isFullyPaid = !isExcessReceived && dueAmount === 0 && netPayableAmount > 0;
  // A receipt written off to the Risk Fund has no due but was never collected,
  // so it must not read as "Paid".
  const isRiskFundReceipt =
    String(receipt?.status ?? '').toUpperCase() === 'RISK_FUND';

  /**
   * Applies an extra-discount edit (amount or ৳/% toggle). When the receipt
   * already carries a Received amount, Received follows the new payable so the
   * receipt stays settled as the discount moves. A blank/0 Received is an
   * intentionally unpaid receipt and is left alone.
   */
  const applyExtraDiscount = (type: 'FLAT' | 'PERCENT', value: number) => {
    setExtraDiscountType(type);
    setExtraDiscountValue(value);
    if (paidAmount <= 0) return;
    const nextExtraDiscount = computeExtraDiscountAmount(type, value, totalAmount);
    const nextCombinedDiscount = Number((totalDiscountAmount + nextExtraDiscount).toFixed(2));
    setPaidAmount(Math.max(0, Number((totalAmount - nextCombinedDiscount).toFixed(2))));
  };

  /**
   * Referral share preview. Mirrors the backend rule: the effective referral %
   * is applied to each test's gross. The counter extra discount is not a
   * commission base and is not shown here.
   */
  const referralProjection = useMemo(() => {
    if (!referralId || selectedItems.length === 0) return null;
    let gross = 0;
    let discount = 0;
    const lines: {
      name: string;
      qty: number;
      gross: number;
      discountPercent: number;
      discount: number;
      share: number;
    }[] = [];
    for (const item of selectedItems) {
      const lineGross = (item.test.price || 0) * item.qty;
      const lineDiscount = lineGross * ((item.discountPercent || 0) / 100);
      const rawShare = (lineGross * effectiveReferralPercent) / 100;
      gross += lineGross;
      discount += lineDiscount;
      lines.push({
        name: item.test.name || 'Test',
        qty: item.qty,
        gross: lineGross,
        discountPercent: item.discountPercent || 0,
        discount: lineDiscount,
        share: Math.min(lineGross, Math.max(0, rawShare)),
      });
    }
    // Hospital revenue after the patient's discount. The commission is a
    // hospital expense paid out of this margin — the patient does not fund it.
    const revenueNet = totalAmount - discount - extraDiscountAmount;
    // Whole-taka payout from the same test-gross base the backend uses, then
    // split across the lines so the breakdown always adds up to the total.
    const doctorShare = Math.min(
      Math.round((gross * effectiveReferralPercent) / 100),
      Math.floor(gross + 1e-9),
    );
    const allocation = allocateWholeTaka(
      doctorShare,
      lines.map((line) => line.share),
    );
    lines.forEach((line, index) => {
      line.share = allocation[index] ?? 0;
    });
    return {
      percent: effectiveReferralPercent,
      discount,
      doctorShare,
      hospitalShare: Math.max(0, Number((revenueNet - doctorShare).toFixed(2))),
      lines,
    };
  }, [
    referralId,
    effectiveReferralPercent,
    selectedItems,
    extraDiscountAmount,
    totalAmount,
    totalDiscountAmount,
  ]);

  // Only surface the positive-share preview; the zero-payout case is handled
  // by its own inline warning and confirmation modal below.
  const referralPreview =
    referralProjection && referralProjection.percent > 0 ? referralProjection : null;

  // A referral is selected but saving would accrue no commission. This is true
  // only when the effective share is 0%. Such a receipt silently produces no
  // payout.
  const referralPayoutIsZero =
    !!referralId &&
    !!referralProjection &&
    Math.round(referralProjection.doctorShare * 100) / 100 <= 0;

  const referralZeroReason = useMemo(() => {
    if (!referralPayoutIsZero) return '';
    if (effectiveReferralPercent <= 0) {
      return 'Referral % is 0%. Set it above 0% to create a referral payout.';
    }
    return 'Referral % is 0%, so no commission will be booked. Raise the share above 0% to create a referral payout. The counter extra discount is never part of the commission base.';
  }, [referralPayoutIsZero, effectiveReferralPercent]);

  /* ── actions ── */
  const handleSelectReferral = (id: string, partner?: ReferralPartner) => {
    setReferralId(id);
    setReferralName(partner?.name || '');
    setReferralType(partner?.type || '');
    // Default share = allocated base discount − the patient's item discount.
    setReferralShareTouched(false);
    setReferralSharePercent(id ? autoReferralPercent : 0);
  };

  const handleClearReferral = () => {
    setReferralId('');
    setReferralName('');
    setReferralType('');
    setReferralShareTouched(false);
    setReferralSharePercent(0);
  };

  const handleClearDoctorReference = () => {
    setDoctorReferenceId('');
    setDoctorReferenceName('');
  };

  const handleReferralCreated = (partner: ReferralPartner) => {
    setShowQuickReferralModal(false);
    handleSelectReferral(partner.id, partner);
  };

  const handleQuickDoctorCreated = (doctor: { id: string; name?: string }) => {
    setShowQuickDoctorModal(false);
    setDoctorReferenceId(doctor.id);
    setDoctorReferenceName(doctor.name || '');
  };
  const addTest = (testId: string) => {
    const test = tests.find((t: any) => t.id === testId);
    setSelectedTests((prev) => new Map(prev).set(testId, { qty: 1, discountPercent: test?.discountPercent || 0 }));
    // Adding a test on an existing receipt follows the same rule as creating
    // one: its linked consumables/containers are billed with it. Dedupe against
    // the lines already on the receipt so nothing is charged twice.
    const links = test?.accessories || [];
    if (links.length > 0) {
      setAccessoryLines((prev) => {
        const next = new Map(prev);
        for (const link of links) {
          const accId = link.diagnosticAccessoryId || link.accessory?.id;
          if (!accId || next.has(accId)) continue;
          next.set(accId, {
            accessoryId: accId,
            name: link.accessory?.name || 'Accessory',
            price: Number(link.accessory?.price) || 0,
            qty: Math.max(1, Number(link.defaultQuantity) || 1),
          });
        }
        return next;
      });
    }
  };

  // Up/Down to move through the available tests, Enter to add the highlighted
  // one to the receipt.
  const testNav = useListKeyboardNav({
    itemCount: orderedTests.length,
    onSelect: (index) => {
      const test = orderedTests[index];
      if (!test || selectedTests.has(test.id)) return;
      addTest(test.id);
    },
    isDisabled: (index) => {
      const test = orderedTests[index];
      return !test || selectedTests.has(test.id);
    },
    resetKey: `${searchQuery}|${activeDepartment}|${testsPage}`,
  });

  const removeTest = (testId: string) => {
    setSelectedTests((prev) => {
      const next = new Map(prev);
      next.delete(testId);
      return next;
    });
  };

  const updateQty = (testId: string, qty: number) => {
    if (qty < 1) {
      removeTest(testId);
      return;
    }
    setSelectedTests((prev) => {
      const next = new Map(prev);
      const existing = next.get(testId);
      next.set(testId, { qty, discountPercent: existing?.discountPercent || 0 });
      return next;
    });
  };

  const updateTestDiscount = (testId: string, discountPercent: number) => {
    setSelectedTests((prev) => {
      const next = new Map(prev);
      const existing = next.get(testId);
      if (existing) {
        next.set(testId, { ...existing, discountPercent: Math.max(0, Math.min(100, discountPercent)) });
      }
      return next;
    });
  };

  const removeAccessory = (accessoryId: string) => {
    setAccessoryLines((prev) => {
      if (!prev.has(accessoryId)) return prev;
      const next = new Map(prev);
      next.delete(accessoryId);
      return next;
    });
  };

  const updateAccessoryQty = (accessoryId: string, qty: number) => {
    setAccessoryLines((prev) => {
      const existing = prev.get(accessoryId);
      if (!existing) return prev;
      const next = new Map(prev);
      if (!Number.isFinite(qty) || qty < 1) {
        next.delete(accessoryId);
      } else {
        next.set(accessoryId, { ...existing, qty });
      }
      return next;
    });
  };

  const handlePrint = async () => {
    if (!id) return;
    setPrintLoading(true);
    setFormError(null);
    try {
      const url = await diagnosticService.printReceipt(id);
      await printPdfToDevice(url, activeDevice, { onError: setFormError });
    } catch (err: any) {
      setFormError(err?.message || 'Failed to generate print preview.');
    } finally {
      setPrintLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    setDownloadLoading(true);
    setFormError(null);
    try {
      const url = await diagnosticService.downloadReceipt(id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${receipt?.receiptNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to download PDF.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSave = () => {
    if (!id) return;
    setFormError(null);
    setSaved(false);

    if (selectedTests.size === 0) {
      setFormError('Please add at least one test');
      return;
    }
    if (canBackdate && !receiptDate) {
      setReceiptDateError('Receipt date & time is required');
      setFormError('Please choose the receipt date & time');
      return;
    }
    setReceiptDateError(undefined);
    if (discountExceedsBase) {
      setShowDiscountLimitWarning(true);
      return;
    }
    if (isExcessReceived) {
      setShowPayableWarning(true);
      return;
    }
    // Gate saving on an explicit acknowledgement: a referral with a 0% share
    // books no distribution, so it never reaches the payout ledger. Warn before
    // the counter saves a receipt that looks referral-linked but pays nobody.
    if (referralPayoutIsZero) {
      setShowReferralPayoutWarning(true);
      return;
    }
    void performSave();
  };

  const handleSaveWithoutPayout = () => {
    setShowReferralPayoutWarning(false);
    void performSave();
  };

  const performSave = async () => {
    if (!id) return;
    const items = Array.from(selectedTests.entries()).map(([diagnosticTestId, data]) => ({
      diagnosticTestId,
      quantity: Number.isFinite(data.qty) && data.qty > 0 ? data.qty : 1,
      discountPercent: data.discountPercent || 0,
    }));
    const accessoryItems = Array.from(accessoryLines.values()).map((a) => ({
      diagnosticAccessoryId: a.accessoryId,
      quantity: Number.isFinite(a.qty) && a.qty > 0 ? a.qty : 1,
    }));
    const itemsChanged = buildItemsSignature(selectedTests) !== originalItemsSignature.current;
    const accessoriesChanged =
      buildAccessorySignature(accessoryItems) !== originalAccessorySignature.current;
    // Only resend the business date when the counter actually changed it, so a
    // plain payment/discount edit never nudges the stored timestamp.
    const receiptDateChanged =
      canBackdate && Boolean(receiptDate) && receiptDate !== originalReceiptDateRef.current;

    try {
      await updateMutation.mutateAsync({
        id,
        data: {
          // Only resend the item lists when one of them actually changed. Sending
          // them on a pure payment/discount edit makes the backend delete and
          // re-create every receipt item, lab/radiology order and specimen —
          // unnecessary work that also requires a visit and fails for older
          // receipts. When they are resent, accessories go with them so the
          // rebuild never drops the accessory charges.
          ...(itemsChanged || accessoriesChanged ? { items, accessoryItems } : {}),
          // Omitted unless a backdate-capable role moved the date: the server
          // then keeps the stored business date untouched.
          ...(receiptDateChanged
            ? { receiptDate: datetimeLocalToISOInTimeZone(receiptDate, hospitalTimezone) }
            : {}),
          paymentMode,
          discountPercent: itemDiscountPercent,
          extraDiscountType,
          extraDiscountValue,
          // Per-receipt ceiling override for roles allowed to raise it. Omitted
          // otherwise so the server falls back to Settings → General.
          baseDiscountPercent: canOverrideBaseDiscount
            ? effectiveBasePercent
            : undefined,
          paidAmount,
          transactionReference: transactionReference || undefined,
          // Promised date for the remaining balance. Omitted when empty so an
          // edit never wipes a date that was set from the counter.
          ...(dueDate ? { dueDate } : {}),
          // Referral + the effective (possibly overridden) share for THIS receipt.
          referralId: referralId || undefined,
          referralSharePercent: referralId ? effectiveReferralPercent : undefined,
          // Reference-only doctor (informational; no revenue impact).
          doctorReferenceId: doctorReferenceId || undefined,
          doctorReferenceName: doctorReferenceName || undefined,
          doctorRecommendation: doctorRecommendation || undefined,
        },
      });
      originalItemsSignature.current = buildItemsSignature(selectedTests);
      originalAccessorySignature.current = buildAccessorySignature(accessoryItems);
      originalReceiptDateRef.current = receiptDate;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      // Mirror the create flow: confirm the save and offer to print straight
      // away, so an edit does not silently complete without the print step.
      setShowSuccessModal(true);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to update receipt. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(id);
      navigate('/opd/diagnostic-receipts');
    } catch (err: any) {
      setDeleteError(err?.message || 'Failed to delete receipt. Please try again.');
    }
  };

  /* ── render a single test row ── */
  const TestRow = ({ test, index }: { test: any; index: number }) => {
    const isSelected = selectedTests.has(test.id);
    const highlighted = index === testNav.highlightedIndex && !isSelected;
    return (
      <button
        data-nav-index={index}
        onMouseEnter={() => testNav.setHighlightedIndex(index)}
        onClick={() => !isSelected && addTest(test.id)}
        disabled={isSelected}
        className={`w-full text-left flex items-center justify-between px-4 py-3 transition-colors border-b border-border last:border-0 ${
          isSelected
            ? 'bg-success/5 cursor-default'
            : highlighted
              ? 'bg-background ring-1 ring-inset ring-primary/40 cursor-pointer'
              : 'hover:bg-background cursor-pointer'
        }`}
      >
        <div className="min-w-0 flex items-center gap-3">
          {isSelected ? (
            <CheckCircle2 size={16} className="text-success shrink-0" />
          ) : (
            <div className="w-4 h-4 rounded border border-border shrink-0" />
          )}
          <div>
            <p className={`text-sm font-medium truncate ${isSelected ? 'text-success' : 'text-text'}`}>
              {test.name}
            </p>
            {(test.room?.name || test.roomNumber) && (
              <p className="text-xs text-text-muted">Room {test.room?.name || test.roomNumber}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          <span className="text-sm font-bold text-primary whitespace-nowrap">
            ৳{test.price?.toLocaleString()}
          </span>
          {!isSelected && (
            <span className="text-xs bg-primary text-white px-2 py-1 rounded whitespace-nowrap">
              Add
            </span>
          )}
        </div>
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load diagnostic receipt</span>
      </div>
    );
  }

  const patient = receipt.patient;
  const isRefunded = receipt.status === 'REFUNDED';
  const preparedBy = `${receipt.createdBy?.firstName || ''} ${receipt.createdBy?.lastName || ''}`.trim();
  const preparedBySignature = receipt.createdBy?.employee?.signatureUrl;

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (location.pathname.startsWith('/diagnostics')) {
                navigate(-1);
              } else {
                navigate('/opd/diagnostic-receipts');
              }
            }}
            className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-text">Edit Receipt {receipt.receiptNumber}</h1>
            <p className="text-sm text-text-muted">Modify tests and update receipt</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() =>
              navigate(
                location.pathname.startsWith('/diagnostics')
                  ? `/diagnostics/receipts/${id}/history`
                  : `/opd/diagnostic-receipts/${id}/history`,
              )
            }
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
          >
            <History size={16} />
            History
          </button>
          <button
            onClick={handlePrint}
            disabled={printLoading}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {printLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
            Print
          </button>
          <button
            onClick={handleDownload}
            disabled={downloadLoading}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {downloadLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Download PDF
          </button>
          <button
            onClick={() => {
              setShowDeleteConfirm(true);
              setDeleteError(null);
            }}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-surface border border-border text-danger rounded-lg hover:bg-danger/10 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {deleteMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            Move to Trash
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <Printer size={13} />
          Print to:
        </span>
        <PrinterTabs defaultId={diagnosticReceiptPrinter?.id} />
      </div>

      {saved && (
        <div className="flex items-center gap-2 p-4 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <CheckCircle2 size={18} />
          <span>Receipt updated successfully.</span>
        </div>
      )}

      {formError && (
        <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{formError}</span>
        </div>
      )}

      {isRefunded && (
        <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
          <AlertCircle size={16} className="shrink-0" />
          <span>This receipt has been refunded and cannot be edited.</span>
        </div>
      )}

      {/* Quick Referral Modal (referral partner) */}
      <QuickReferralModal
        open={showQuickReferralModal}
        initialQuery={quickReferralQuery}
        onClose={() => setShowQuickReferralModal(false)}
        onCreated={handleReferralCreated}
      />

      {/* Quick Add Doctor Modal (doctor reference) */}
      <QuickDoctorModal
        open={showQuickDoctorModal}
        initialQuery={quickDoctorQuery}
        onClose={() => setShowQuickDoctorModal(false)}
        onCreated={handleQuickDoctorCreated}
      />

      {/* Same patient already owes on another receipt — offer to add tests there */}
      <DueReceiptPromptModal
        open={
          !skipDueReceiptPrompt && !isRefunded && receiptHasBalance && patientDueReceipts.length > 0
        }
        mode="edit"
        patientName={patient ? joinFullName(patient) : undefined}
        patientMobile={patient?.mobileNumber || patient?.mobile}
        receipts={patientDueReceipts}
        targetReceiptNumber={receipt.receiptNumber}
        onMerge={async () => {
          await mergeMutation.mutateAsync({
            targetReceiptId: receipt.id,
            sourceReceiptIds: patientDueReceipts.map((other) => other.id),
          });
          setSkipDueReceiptPrompt(true);
        }}
        onOpenReceipt={(receiptId) =>
          navigate(`/opd/diagnostic-receipts/${receiptId}`, {
            state: { openedFromDuePrompt: true },
          })
        }
        onSkip={() => setSkipDueReceiptPrompt(true)}
      />

      {/* Received-too-high warning — blocks saving until the amount is valid */}
      <ReceivedExcessModal
        open={showPayableWarning}
        details={{ totalAmount, discountAmount: patientDiscountAmount, netPayable: netPayableAmount, received: paidAmount, excess: excessAmount }}
        onClose={() => setShowPayableWarning(false)}
        onSetToPayable={() => {
          setPaidAmount(netPayableAmount);
          setShowPayableWarning(false);
        }}
      />

      {/* Discount above the Receipt Base Discount — blocks saving until fixed */}
      <ReceiptDiscountLimitModal
        open={showDiscountLimitWarning}
        details={{
          discountPercent: combinedDiscountPercent,
          basePercent: effectiveBasePercent,
        }}
        onClose={() => setShowDiscountLimitWarning(false)}
      />

      {/* Referral selected but no payout would be booked — requires confirmation */}
      <ReferralZeroPayoutModal
        open={showReferralPayoutWarning}
        details={{
          referralName,
          referralPercent: effectiveReferralPercent,
          projectedPayout: referralProjection?.doctorShare ?? 0,
          reason: referralZeroReason,
        }}
        onClose={() => setShowReferralPayoutWarning(false)}
        onProceed={handleSaveWithoutPayout}
      />

      {/* Referral calculation breakdown — opened from the "i" button beside Referral (%) */}
      {referralPreview && (
        <ReferralCalculationModal
          open={showReferralInfo}
          onClose={() => setShowReferralInfo(false)}
          details={{
            gross: testsTotalAmount,
            percent: referralPreview.percent,
            lines: referralPreview.lines,
            totalDiscount: referralPreview.discount,
            totalShare: referralPreview.doctorShare,
            hospitalShare: referralPreview.hospitalShare,
          }}
        />
      )}

      {/* Update Success Modal — mirrors the create flow with a Print action */}
      {showSuccessModal && receipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-sm space-y-4 shadow-xl mx-4">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle2 size={24} className="text-success" />
              </div>
              <h3 className="text-lg font-bold text-text">Receipt Updated!</h3>
              <p className="text-sm text-text-muted">
                Receipt <span className="font-mono font-medium text-text">{receipt.receiptNumber}</span> has been updated successfully.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={async () => {
                  await handlePrint();
                  setShowSuccessModal(false);
                }}
                disabled={printLoading}
                className="px-4 py-2.5 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {printLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                Print
              </button>
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                Go
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border p-6 max-w-sm w-full space-y-4">
            <h3 className="text-lg font-semibold text-text">Move to Trash</h3>
            <p className="text-sm text-text-muted">
              Move receipt{' '}
              <strong className="text-text">{receipt.receiptNumber}</strong> to the trash? It and its
              associated lab and radiology orders will be removed from the active dashboard, and you
              can restore it later from the Trash.
            </p>
            {deleteError && (
              <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{deleteError}</span>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark transition-colors text-sm font-medium disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient & Visit (read-only) */}
      <div className="bg-surface rounded-xl border border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          {/* Patient */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <User size={15} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] leading-tight text-text-muted">Patient</p>
              <p className="truncate text-sm font-medium leading-tight text-text">
                {patient ? joinFullName(patient) : 'N/A'}
                {patient?.mobile && (
                  <span className="font-normal text-text-muted"> · {patient.mobile}</span>
                )}
              </p>
            </div>
          </div>

          <div className="hidden h-8 w-px bg-border sm:block" />

          {/* Receipt Number */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Hash size={15} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] leading-tight text-text-muted">Receipt No.</p>
              <p className="truncate font-mono text-sm font-medium leading-tight text-text">
                {receipt.receiptNumber}
              </p>
            </div>
          </div>

          <div className="hidden h-8 w-px bg-border sm:block" />

          {/* Date */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Calendar size={15} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] leading-tight text-text-muted">Date</p>
              <p className="whitespace-nowrap text-sm font-medium leading-tight text-text">
                {new Date(receipt.createdAt).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="hidden h-8 w-px bg-border sm:block" />

          {/* Status (workflow-driven, read-only) */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Activity size={15} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] leading-tight text-text-muted">Status</p>
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase ${
                  receipt.status === 'ACTIVE'
                    ? 'bg-success/10 text-success'
                    : receipt.status === 'USED'
                      ? 'bg-primary/10 text-primary'
                      : receipt.status === 'REFUNDED'
                        ? 'bg-danger/10 text-danger'
                        : 'bg-warning/10 text-warning'
                }`}
              >
                {receipt.status?.replace('_', ' ') || 'ACTIVE'}
              </span>
            </div>
          </div>

          {/* Prepared by (pushed to the end) */}
          {preparedBy && (
            <div className="flex items-center gap-2.5 min-w-0 sm:ml-auto">
              {preparedBySignature && (
                <div className="h-8 w-16 shrink-0 rounded border border-border bg-white p-0.5">
                  <img
                    src={preparedBySignature}
                    alt="Prepared by signature"
                    className="h-full w-full object-contain object-left-bottom"
                  />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[11px] leading-tight text-text-muted">Prepared by</p>
                <p className="truncate text-sm font-medium leading-tight text-text">{preparedBy}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Grid: Browse Tests + Receipt Summary */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">

        {/* Left: Browse Tests */}
        <div className="xl:col-span-2 space-y-6">

          {/* Search + Department Filters */}
          <div className="bg-surface rounded-xl border border-border p-6 space-y-4">
            <div className="flex items-center gap-2">
              <ClipboardList size={18} className="text-primary" />
              <h2 className="text-sm font-semibold text-text uppercase tracking-wide">Available Tests</h2>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={testNav.onKeyDown}
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                placeholder="Search by test name..."
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Department chips */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveDepartment('ALL')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  activeDepartment === 'ALL'
                    ? 'bg-primary text-white'
                    : 'bg-background border border-border text-text-muted hover:text-text'
                }`}
              >
                All Departments
              </button>
              {departments.map((dept) => (
                <button
                  key={dept.id}
                  onClick={() => setActiveDepartment(dept.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeDepartment === dept.id
                      ? 'bg-primary text-white'
                      : 'bg-background border border-border text-text-muted hover:text-text'
                  }`}
                >
                  {dept.name} <span className="opacity-70">({dept.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tests List */}
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            <div ref={testNav.listRef} className="p-6">
              {totalFilteredTests === 0 ? (
                <div className="text-center py-10">
                  <FlaskConical className="w-8 h-8 text-text-muted mx-auto mb-2" />
                  <p className="text-sm text-text-muted">No tests found</p>
                </div>
              ) : activeDepartment === 'ALL' && groupedTests ? (
                <div className="space-y-6">
                  {groupedTests.map((group) => (
                    <div key={group.dept.id}>
                      <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2 px-1">
                        {group.dept.name}
                      </h3>
                      <div className="border border-border rounded-lg overflow-hidden">
                        {group.tests.map((test: any) => (
                          <TestRow key={test.id} test={test} index={testNavIndex.get(test.id) ?? 0} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden">
                  {pagedTests.map((test: any) => (
                    <TestRow key={test.id} test={test} index={testNavIndex.get(test.id) ?? 0} />
                  ))}
                </div>
              )}
            </div>
            <Pagination
              page={testsPage}
              totalPages={totalTestPages}
              total={totalFilteredTests}
              limit={TESTS_PER_PAGE}
              onPageChange={setTestsPage}
              itemName="tests"
            />
          </div>
        </div>

        {/* Right: Receipt Summary */}
        <div className="xl:col-span-1">
          <div className="sticky top-6 bg-surface rounded-xl border border-border p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Receipt size={18} className="text-primary" />
              <h2 className="text-sm font-semibold text-text uppercase tracking-wide">Receipt Summary</h2>
            </div>

            {/* Receipt date & time — the receipt's business date (createdAt). */}
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-medium text-text-muted uppercase">
                Receipt Date &amp; Time *
                <InfoHint
                  label="Receipt Date & Time"
                  en="The date the receipt is posted on. Changing it (backdating) requires the “Add Backdate Receipt” permission; otherwise the receipt keeps its stored date."
                  bn="রিসিট যে তারিখে পোস্ট হয়েছে। পরিবর্তন (ব্যাকডেট) করতে “Add Backdate Receipt” পারমিশন প্রয়োজন; না থাকলে রিসিটের সংরক্ষিত তারিখ অপরিবর্তিত থাকে।"
                />
              </label>
              <DateTimePicker
                value={receiptDate}
                onChange={(value) => {
                  setReceiptDate(value);
                  if (value) setReceiptDateError(undefined);
                }}
                error={receiptDateError}
                timeZone={hospitalTimezone}
                disabled={!canBackdate || isRefunded}
                clearable={canBackdate && !isRefunded}
                required
              />
              <p className="mt-1 flex items-center gap-1 text-[10px] leading-4 text-text-muted">
                <Globe size={10} className="shrink-0" />
                {canBackdate
                  ? `Times shown in ${hospitalTimezone ?? DEFAULT_HOSPITAL_TIMEZONE} (Settings → General)`
                  : 'Read-only. Requires the “Add Backdate Receipt” permission to change.'}
              </p>
            </div>

            {/* Selected tests */}
            {selectedItems.length === 0 && accessoryItems.length === 0 ? (
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Receipt className="w-8 h-8 text-text-muted mx-auto mb-2" />
                <p className="text-sm text-text-muted font-medium">No tests added yet</p>
                <p className="text-xs text-text-muted mt-1">Browse tests on the left to add</p>
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-background border-b border-border">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Test</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-text-muted uppercase w-16">Qty</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Disc%</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                      <th className="px-1 py-2 text-center w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {selectedItems.map(({ testId, qty, discountPercent, test }) => (
                      <tr key={testId} className="hover:bg-background/50">
                        <td className="px-3 py-2.5">
                          <p className="font-medium text-text text-xs">{test.name}</p>
                          <p className="text-[10px] text-text-muted">{test.department?.name}</p>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <input
                            type="number"
                            min={1}
                            value={qty}
                            onChange={(e) => updateQty(testId, Number(e.target.value))}
                            disabled={isRefunded}
                            className="w-12 px-1 py-1 text-xs rounded border border-border bg-background text-center focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                          />
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <ValidatedNumberInput
                            value={discountPercent}
                            onChange={(v) => updateTestDiscount(testId, v)}
                            disabled={isRefunded}
                            max={100}
                            className="w-14 px-1 py-1 text-xs rounded border border-border bg-background text-center focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                          />
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-text text-xs whitespace-nowrap">
                          <div>৳{(test.price * qty).toLocaleString()}</div>
                          {discountPercent > 0 && (
                            <div className="text-[10px] text-danger">-৳{Math.round((test.price * qty * discountPercent / 100)).toLocaleString()}</div>
                          )}
                        </td>
                        <td className="px-1 py-2.5 text-center">
                          <button
                            onClick={() => removeTest(testId)}
                            disabled={isRefunded}
                            className="p-1 rounded hover:bg-danger/10 text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {accessoryItems.map(({ accessoryId, name, price, qty }) => (
                      <tr key={accessoryId} className="hover:bg-background/50 bg-warning/5">
                        <td className="px-3 py-2.5">
                          <p className="font-medium text-text text-xs">{name}</p>
                          <p className="text-[10px] text-text-muted">Accessory</p>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <input
                            type="number"
                            min={1}
                            value={qty}
                            onChange={(e) => updateAccessoryQty(accessoryId, Number(e.target.value))}
                            disabled={isRefunded}
                            className="w-12 px-1 py-1 text-xs rounded border border-border bg-background text-center focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                          />
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span className="text-xs text-text-muted">0.00</span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-text text-xs whitespace-nowrap">
                          ৳{(price * qty).toLocaleString()}
                        </td>
                        <td className="px-1 py-2.5 text-center">
                          <button
                            onClick={() => removeAccessory(accessoryId)}
                            disabled={isRefunded}
                            className="p-1 rounded hover:bg-danger/10 text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(selectedItems.length > 0 || accessoryItems.length > 0) && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-muted">
                  {selectedItems.length} test(s) · {accessoryItems.length} accessory(s)
                </span>
                <button
                  onClick={() => {
                    setSelectedTests(new Map());
                    setAccessoryLines(new Map());
                  }}
                  disabled={isRefunded}
                  className="text-xs text-danger hover:underline font-medium disabled:opacity-50"
                >
                  Clear all
                </button>
              </div>
            )}

            <div className="h-px bg-border" />

            {/* Doctor Reference — informational only (no revenue share) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="flex items-center gap-1 text-xs font-medium text-text-muted uppercase">
                  Doctor Reference
                  <InfoHint
                    label="Doctor Reference"
                    en="A reference-only doctor for this receipt. It is informational and never part of any revenue share or payout."
                    bn="এই রিসিটের জন্য শুধুমাত্র তথ্যের রেফারেন্স ডাক্তার। এটি কোনো রেভিনিউ শেয়ার বা পেমেন্টের অংশ নয়।"
                  />
                </label>
                {doctorReferenceId && !isRefunded && (
                  <button
                    type="button"
                    onClick={handleClearDoctorReference}
                    className="text-xs font-medium text-danger hover:underline uppercase"
                  >
                    Clear
                  </button>
                )}
              </div>
              <DoctorSearchSelect
                value={doctorReferenceId}
                onChange={(id, doc) => {
                  setDoctorReferenceId(id);
                  setDoctorReferenceName(doc?.name || '');
                }}
                selectedLabel={doctorReferenceName}
                showPreview={false}
                disabled={isRefunded}
                placeholder="Search doctor by name or code..."
                onAddNew={(query) => {
                  setQuickDoctorQuery(query);
                  setShowQuickDoctorModal(true);
                }}
                addNewLabel="Add new doctor"
              />
            </div>

            {/* Referral Type + Referral + Referral % (one row) */}
            <div className="grid grid-cols-[90px_minmax(0,1fr)_104px] gap-2 items-start">
              <div>
                <label className="block text-xs font-medium text-text-muted uppercase mb-1">Type</label>
                <select
                  value={referralTypeFilter}
                  disabled={isRefunded}
                  onChange={(e) =>
                    setReferralTypeFilter(e.target.value as 'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER')
                  }
                  className="w-full px-2 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-60"
                >
                  <option value="ALL">All</option>
                  <option value="DOCTOR">Doctor</option>
                  <option value="EMPLOYEE">Employee</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between gap-1 mb-1">
                  <label className="text-xs font-medium text-text-muted uppercase">Referral</label>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {referralId && (
                      <span className="text-[10px] text-text-muted uppercase">
                        {referralType === 'DOCTOR' ? 'Doctor' : referralType === 'EMPLOYEE' ? 'Employee' : 'Other'}
                      </span>
                    )}
                    {referralId && (
                      <button
                        type="button"
                        onClick={handleClearReferral}
                        className="text-[10px] font-medium text-danger hover:underline uppercase shrink-0"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <ReferralSearchSelect
                  value={referralId}
                  onChange={handleSelectReferral}
                  selectedLabel={referralName}
                  type={referralTypeFilter}
                  placeholder="Search referral..."
                  disabled={isRefunded}
                  onAddNew={(query) => {
                    setQuickReferralQuery(query);
                    setShowQuickReferralModal(true);
                  }}
                  addNewLabel="Add referral"
                />
              </div>
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-text-muted mb-1 uppercase">
                  Referral %
                  <InfoHint
                    label="Referral %"
                    en="Share of the TEST bill paid to the referral. It defaults to the hospital's allocated base discount minus the patient's item discount (e.g. base 40% − patient 20% = referral 20%). Paid by the hospital, never added to the patient's bill. Overrides the auto share for this receipt only."
                    bn="রেফারেল টেস্ট বিলের যে অংশ পাবে। ডিফল্টভাবে হাসপাতালের বরাদ্দ বেস ডিসকাউন্ট থেকে রোগীর আইটেম ডিসকাউন্ট বাদ দিয়ে হিসাব হয় (যেমন বেস ৪০% − রোগী ২০% = রেফারেল ২০%)। হাসপাতাল এটি বহন করে; রোগীর বিলে যোগ হয় না। শুধুমাত্র এই রিসিটের জন্য অটো শতাংশ পরিবর্তন করে।"
                  />
                </label>
                <div className="flex items-center gap-1">
                  <ValidatedNumberInput
                    value={referralSharePercent}
                    onChange={(value) => {
                      setReferralShareTouched(true);
                      setReferralSharePercent(value);
                    }}
                    disabled={!referralId || isRefunded}
                    max={100}
                    placeholder="0.00"
                    className={`w-full px-3 py-2 rounded-lg border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-60 ${
                      referralPayoutIsZero ? 'border-warning ring-1 ring-warning/40' : 'border-border'
                    }`}
                  />
                  <span className="text-sm text-text-muted">%</span>
                </div>
                {referralPayoutIsZero && (
                  <p className="text-[10px] text-warning mt-1 leading-snug">
                    No payout will be created in Billing → Payouts.
                  </p>
                )}
              </div>
            </div>

            {referralId && effectiveReferralPercent > 0 && totalDiscountAmount > 0 && (
              <p className="text-[11px] text-text-muted leading-relaxed">
                <span className="font-medium text-text">Referral:</span>{' '}
                {Number(effectiveReferralPercent.toFixed(2))}% of the test bill
                {' '}(base {effectiveBasePercent}% − patient {Number(itemDiscountPercent.toFixed(2))}%) ={' '}
                ৳{referralCommissionAmount.toLocaleString()} · paid by the hospital
              </p>
            )}

            {/* Note — same multi-line field as the create page */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Note</label>
              <textarea
                value={doctorRecommendation}
                onChange={(e) => setDoctorRecommendation(e.target.value)}
                disabled={isRefunded}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm min-h-[80px] resize-y disabled:opacity-50"
                placeholder="Prescribed tests, urgency..."
                rows={3}
              />
            </div>

            <div className="h-px bg-border" />

            {/* Payment */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Payment Mode</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                disabled={isRefunded}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-50"
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Transaction Ref</label>
              <input
                type="text"
                value={transactionReference}
                onChange={(e) => setTransactionReference(e.target.value)}
                disabled={isRefunded}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-50"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="flex items-center gap-1 text-xs font-medium text-text-muted mb-1 uppercase">
                Due By
                <InfoHint
                  label="Due By"
                  en="Promised date for the outstanding balance. Tracking only — the collected amount is always dated on the actual payment date."
                  bn="বাকি টাকা পরিশোধের প্রতিশ্রুত তারিখ। শুধুমাত্র ট্র্যাকিংয়ের জন্য — আদায় করা টাকা সর্বদা প্রকৃত পেমেন্টের তারিখে গণ্য হয়।"
                />
              </label>
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                disabled={isRefunded}
                placeholder="No promised date"
                clearable={false}
              />
              {dueDate && new Date(dueDate).getTime() < new Date(new Date().toDateString()).getTime() && dueAmount > 0 && (
                <p className="text-[10px] text-danger mt-1 leading-snug">Overdue</p>
              )}
            </div>

            <div className="h-px bg-border" />

            {/* Discount & Received */}
            {canOverrideBaseDiscount && !isRefunded && (
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-text-muted mb-1 uppercase">
                  Receipt Base Discount (%)
                  <InfoHint
                    label="Receipt Base Discount (%)"
                    en="Maximum discount allowed on this receipt. It overrides the hospital-wide Receipt Base Discount in Settings → General for this receipt only and defaults to 100%."
                    bn="এই রিসিটে সর্বোচ্চ অনুমোদিত ডিসকাউন্ট। এটি শুধু এই রিসিটের জন্য Settings → General-এর Receipt Base Discount-কে ওভাররাইড করে এবং ডিফল্ট ১০০%।"
                  />
                </label>
                <div className="flex items-center gap-1">
                  <ValidatedNumberInput
                    value={baseDiscountOverride}
                    onChange={(value) => setBaseDiscountOverride(value)}
                    max={100}
                    placeholder="100"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                  <span className="text-sm text-text-muted">%</span>
                </div>
                <p className="text-[10px] text-text-muted mt-1 leading-snug">
                  Overrides the {referralBasePercent}% from Settings → General for this receipt.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Discount %</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={itemDiscountPercent}
                  readOnly
                  disabled={isRefunded}
                  className={`w-full px-3 py-2 rounded-lg border bg-background/50 text-text focus:outline-none text-sm cursor-not-allowed disabled:opacity-50 ${
                    discountExceedsBase ? 'border-danger ring-1 ring-danger/40' : 'border-border'
                  }`}
                />
                {discountExceedsBase && (
                  <p className="text-[11px] text-danger mt-1">
                    Max {effectiveBasePercent}% (Receipt Base Discount). Reduce the discount to save.
                  </p>
                )}
                {referralCommissionAmount > 0 && (
                  <p className="text-[10px] text-text-muted mt-1 leading-snug">
                    The patient keeps this full discount; the {effectiveReferralPercent}% referral is paid by the hospital.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Extra Discount</label>
                <div className="flex items-stretch gap-1.5">
                  <ValidatedNumberInput
                    value={extraDiscountValue}
                    onChange={(value) => applyExtraDiscount(extraDiscountType, value)}
                    disabled={isRefunded}
                    max={extraDiscountType === 'PERCENT' ? 100 : undefined}
                    placeholder="0"
                    className="w-full min-w-0 px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-50"
                  />
                  <div className="inline-flex shrink-0 rounded-lg border border-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => applyExtraDiscount('FLAT', extraDiscountValue)}
                      disabled={isRefunded}
                      title="Flat amount"
                      className={`px-2.5 text-xs font-bold transition-colors disabled:opacity-50 ${
                        extraDiscountType === 'FLAT'
                          ? 'bg-primary text-white'
                          : 'bg-background text-text-muted hover:text-text'
                      }`}
                    >
                      ৳
                    </button>
                    <button
                      type="button"
                      onClick={() => applyExtraDiscount('PERCENT', extraDiscountValue)}
                      disabled={isRefunded}
                      title="Percentage"
                      className={`px-2.5 text-xs font-bold border-l border-border transition-colors disabled:opacity-50 ${
                        extraDiscountType === 'PERCENT'
                          ? 'bg-primary text-white'
                          : 'bg-background text-text-muted hover:text-text'
                      }`}
                    >
                      %
                    </button>
                  </div>
                </div>
                {extraDiscountType === 'PERCENT' && extraDiscountAmount > 0 && (
                  <p className="text-[10px] text-text-muted mt-1">
                    = ৳{extraDiscountAmount.toLocaleString()} off
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="flex items-center gap-1 text-xs font-medium text-text-muted uppercase">
                    Received
                    <InfoHint
                      label="Received"
                      en="Amount actually collected from the patient. Once an amount is entered, it follows the Extra Discount so the receipt stays settled; a blank or 0 Received (unpaid receipt) is left at 0."
                      bn="রোগীর কাছ থেকে প্রকৃত আদায়কৃত টাকা। একবার টাকা লেখা হলে Extra Discount পরিবর্তনের সাথে এটি নতুন প্রদেয় অনুযায়ী আপডেট হয়; খালি বা ০ রাখলে (বাকি রিসিট) ০-ই থাকে।"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setPaidAmount(netPayableAmount)}
                    disabled={netPayableAmount <= 0 || isRefunded}
                    className="text-[10px] font-medium text-primary hover:underline uppercase disabled:opacity-50 disabled:no-underline"
                  >
                    Autofill ৳{netPayableAmount.toLocaleString()}
                  </button>
                </div>
                <ValidatedNumberInput
                  value={paidAmount}
                  onChange={(value) => setPaidAmount(Math.max(0, value))}
                  disabled={isRefunded}
                  placeholder="0"
                  className={`w-full px-3 py-2 rounded-lg border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-50 ${
                    isExcessReceived ? 'border-danger ring-1 ring-danger/40' : 'border-border'
                  }`}
                />
                {isExcessReceived ? (
                  <p className="text-[11px] text-danger mt-1">
                    Received exceeds the payable ৳{netPayableAmount.toLocaleString()} by ৳
                    {excessAmount.toLocaleString()}. Reduce it before saving.
                  </p>
                ) : (
                  <p className="text-[11px] text-text-muted mt-1">
                    Max ৳{netPayableAmount.toLocaleString()} (after discount)
                  </p>
                )}
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-text">Total Amount</span>
              <span className="text-2xl font-bold text-primary">৳{totalAmount.toLocaleString()}</span>
            </div>
            <div className="space-y-1">
              {referralCommissionAmount > 0 && totalDiscountAmount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">
                    Item discount (
                    <span className="text-primary font-semibold">{itemDiscountPercent}%</span>)
                  </span>
                  <span className="text-text font-medium">৳{totalDiscountAmount.toLocaleString()}</span>
                </div>
              )}
              {referralPreview && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted inline-flex items-center gap-1">
                    Referral · hospital pays (
                    <span className="text-primary font-semibold">
                      {Number(referralPreview.percent.toFixed(2))}%
                    </span>{' '}
                    of the test bill
                    )
                    <button
                      type="button"
                      onClick={() => setShowReferralInfo(true)}
                      aria-label="How is this referral amount calculated?"
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-primary/10 hover:text-primary"
                    >
                      <Info size={12} />
                    </button>
                  </span>
                  <span className="text-primary font-medium">৳{Math.round(referralPreview.doctorShare).toLocaleString()}</span>
                </div>
              )}
              <div className="space-y-1">
                {totalDiscountAmount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      Discount (<span className="text-primary font-semibold">{itemDiscountPercent}%</span>)
                    </span>
                    <span className="text-danger font-medium">-৳{Number(totalDiscountAmount.toFixed(2)).toLocaleString()}</span>
                  </div>
                )}
                {extraDiscountAmount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      Extra Discount
                      {extraDiscountType === 'PERCENT' && (
                        <>
                          {' ('}
                          <span className="text-primary font-semibold">{extraDiscountValue}%</span>
                          {')'}
                        </>
                      )}
                    </span>
                    <span className="text-danger font-medium">-৳{extraDiscountAmount.toLocaleString()}</span>
                  </div>
                )}
                {combinedDiscountAmount > 0 && (totalDiscountAmount > 0 || extraDiscountAmount > 0) && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      Total Discount (
                      <span className="text-primary font-semibold">{combinedDiscountPercent}%</span>)
                    </span>
                    <span className="text-danger font-bold">-৳{combinedDiscountAmount.toLocaleString()}</span>
                  </div>
                )}
              </div>
              {paidAmount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Received</span>
                  <span className="text-success font-medium">৳{paidAmount.toLocaleString()}</span>
                </div>
              )}
            </div>

            {totalAmount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-muted">
                  {isRiskFundReceipt || isFullyPaid ? 'Status' : isExcessReceived ? 'Excess Received' : 'Due'}
                </span>
                {isRiskFundReceipt ? (
                  <span className="text-warning font-bold inline-flex items-center gap-1">
                    <Info size={14} />
                    Risk Fund
                  </span>
                ) : isFullyPaid ? (
                  <span className="text-success font-bold inline-flex items-center gap-1">
                    <CheckCircle2 size={14} />
                    Paid
                  </span>
                ) : isExcessReceived ? (
                  <span className="text-danger font-bold">৳{excessAmount.toLocaleString()}</span>
                ) : (
                  <span className="text-text font-bold">৳{dueAmount.toLocaleString()}</span>
                )}
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={selectedTests.size === 0 || updateMutation.isPending || isRefunded}
              className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {updateMutation.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Save size={18} />
              )}
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
